import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size as sizeMiddleware,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import moment from "moment";
import dateFormats from "app/data/common/dateFormats";
import Button, { ButtonThemes } from "ui-components/button/button";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import {
  DATE_RANGE_PRESET_KEYS,
  DATE_RANGE_PRESET_LABELS,
  DateRangePresetKey,
  detectPresetFromRange,
  formatDisplayRange,
  getDateRangeForPreset,
  parseUiDate,
  toUiDateString,
} from "./dateRangePresets";
import "react-datepicker/dist/react-datepicker.css";
import "./xgsDateRangePickerWithPresets.scss";

export interface XGSDateRangePickerWithPresetsProps {
  start?: string;
  end?: string;
  onApply: (start?: string, end?: string) => void;
  className?: string;
  maxDate?: Date;
  placeholder?: string;
}

const XGSDateRangePickerWithPresets: React.FC<XGSDateRangePickerWithPresetsProps> = ({
  start,
  end,
  onApply,
  className = "",
  maxDate = new Date(),
  placeholder = "Select date range",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draftStart, setDraftStart] = useState<string | undefined>(start);
  const [draftEnd, setDraftEnd] = useState<string | undefined>(end);
  const [activePreset, setActivePreset] = useState<DateRangePresetKey | null>(null);
  const [calendarKey, setCalendarKey] = useState(0);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    strategy: "fixed",
    placement: "left-start",
    onOpenChange: setIsOpen,
    middleware: [
      offset(8),
      flip({ fallbackPlacements: ["bottom-end", "bottom-start", "right-start"] }),
      shift({ padding: 8 }),
      sizeMiddleware({
        padding: 8,
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.max(320, availableHeight)}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, { ancestorScroll: true });
  const role = useRole(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  useEffect(() => {
    if (!isOpen) {
      setDraftStart(start);
      setDraftEnd(end);
      setActivePreset(detectPresetFromRange(start, end));
    }
  }, [isOpen, start, end]);

  const draftStartDate = useMemo(() => parseUiDate(draftStart)?.toDate(), [draftStart]);
  const draftEndDate = useMemo(() => parseUiDate(draftEnd)?.toDate(), [draftEnd]);

  const displayLabel = useMemo(() => {
    const label = formatDisplayRange(start, end);
    return label === "Select date range" ? placeholder : label;
  }, [start, end, placeholder]);

  const resetCalendarSelection = () => setCalendarKey((key) => key + 1);

  const handlePresetSelect = (preset: DateRangePresetKey) => {
    handleClear();
    const range = getDateRangeForPreset(preset);
    setDraftStart(toUiDateString(range.start));
    setDraftEnd(toUiDateString(range.end));
    setActivePreset(preset);
    resetCalendarSelection();
  };

  const handleCalendarChange = (dates: [Date | null, Date | null]) => {
    const [nextStart, nextEnd] = dates;
    const nextStartStr = nextStart ? moment(nextStart).format(dateFormats.uiDate) : undefined;
    const nextEndStr = nextEnd ? moment(nextEnd).format(dateFormats.uiDate) : undefined;

    setDraftStart(nextStartStr);
    setDraftEnd(nextEndStr);
    setActivePreset(detectPresetFromRange(nextStartStr, nextEndStr));
  };

  const handleManualInputChange = (field: "start" | "end", value: string) => {
    const trimmed = value.trim();
    const nextStart = field === "start" ? (trimmed || undefined) : draftStart;
    const nextEnd = field === "end" ? (trimmed || undefined) : draftEnd;

    if (field === "start") setDraftStart(trimmed || undefined);
    if (field === "end") setDraftEnd(trimmed || undefined);
    setActivePreset(detectPresetFromRange(nextStart, nextEnd));
  };

  const handleCancel = () => {
    setDraftStart(start);
    setDraftEnd(end);
    setActivePreset(detectPresetFromRange(start, end));
    setIsOpen(false);
  };

  const handleApply = () => {
    onApply(draftStart, draftEnd);
    setIsOpen(false);
  };

  const handleClear = () => {
    setDraftStart(undefined);
    setDraftEnd(undefined);
    setActivePreset(null);
    resetCalendarSelection();
  };

  const hasAppliedRange = Boolean(start || end);

  return (
    <div className={`xgs-date-range-picker ${className}`.trim()}>
      <button
        type="button"
        ref={refs.setReference}
        className={`xgs-date-range-picker__trigger${isOpen ? " xgs-date-range-picker__trigger--open" : ""}${hasAppliedRange ? " xgs-date-range-picker__trigger--active" : ""}`}
        {...getReferenceProps()}
      >
        <span className="xgs-date-range-picker__trigger-label">{displayLabel}</span>
        <XGSIcon
          icon={isOpen ? XGSIcons.faCaretUp : XGSIcons.faCaretDown}
          className="xgs-date-range-picker__trigger-icon"
          size="sm"
        />
      </button>

      {isOpen && (
        <FloatingPortal>
        <FloatingFocusManager context={context} modal={false}>
          <div
            ref={refs.setFloating}
            className="xgs-date-range-picker__panel"
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <div className="xgs-date-range-picker__inputs">
              <input
                type="text"
                className="xgs-date-range-picker__input"
                placeholder="MM/DD/YYYY"
                value={draftStart ?? ""}
                onChange={(e) => handleManualInputChange("start", e.target.value)}
              />
              <span className="xgs-date-range-picker__inputs-divider">–</span>
              <input
                type="text"
                className="xgs-date-range-picker__input"
                placeholder="MM/DD/YYYY"
                value={draftEnd ?? ""}
                onChange={(e) => handleManualInputChange("end", e.target.value)}
              />
            </div>

            <div className="xgs-date-range-picker__body">
              <div className="xgs-date-range-picker__calendar">
                <DatePicker
                  key={calendarKey}
                  selected={draftStartDate}
                  startDate={draftStartDate}
                  endDate={draftEndDate}
                  onChange={handleCalendarChange}
                  selectsRange
                  inline
                  monthsShown={2}
                  maxDate={maxDate}
                  calendarStartDay={1}
                  shouldCloseOnSelect={false}
                  focusSelectedMonth={false}
                />
              </div>

              <div className="xgs-date-range-picker__presets">
                {DATE_RANGE_PRESET_KEYS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={`xgs-date-range-picker__preset${activePreset === preset ? " xgs-date-range-picker__preset--active" : ""}`}
                    onClick={() => handlePresetSelect(preset)}
                  >
                    {activePreset === preset && (
                      <XGSIcon icon={XGSIcons.faCheck} className="xgs-date-range-picker__preset-check" size="sm" />
                    )}
                    <span>{DATE_RANGE_PRESET_LABELS[preset]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="xgs-date-range-picker__footer">
              <button
                type="button"
                className="xgs-date-range-picker__clear"
                onClick={handleClear}
              >
                Clear
              </button>
              <div className="xgs-date-range-picker__footer-actions">
                <Button type="button" theme={ButtonThemes.gray} onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type="button" theme={ButtonThemes.blue} onClick={handleApply}>
                  Search
                </Button>
              </div>
            </div>
          </div>
        </FloatingFocusManager>
        </FloatingPortal>
      )}
    </div>
  );
};

export default XGSDateRangePickerWithPresets;
